import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Form, Col, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import Api from "../../components/api/Api";
import Notification, { showToast } from "../../../components/common/notification/Notification";
import LocationSelector from "../../../components/customer/LocationSelector";

const LocationServiceSelector = ({ userId }) => {
  const [profile, setProfile] = useState({
    id: userId,
  });
  const [locationValue, setLocationValue] = useState({
    country: "",
    state: "",
    city: "",
    area: "",
  });
  const [services, setServices] = useState([]);

  const loggedInUser = useSelector((state) => state.auth.user);
  const editUserApi = Api({ url: `admin/editUser` });
  const loggedInUserToken = loggedInUser?.refreshToken;
  const baseUrl = process.env.REACT_APP_BASE_URL;

  const fetchUserData = useCallback(async () => {
    if (userId) {
      try {
        const res = await axios.get(`${baseUrl}/admin/user/id/${userId}`, {
          headers: {
            Authorization: `Bearer ${loggedInUserToken}`,
          },
        });
        const user = res.data.user;
        const currentCountryId = user.cities?.[0]?.state?.country?.id || "";
        const currentStateId = user.states?.[0]?.id || "";
        const currentCityId = user.cities?.[0]?.id || "";

        setProfile({
          id: userId,
          state_id: user.states[0]?.id || "",
          city_id: user.cities[0]?.id || "",
          country_id: currentCountryId || "",
          service_id: user.service_id || "",
          first_name: user.first_name,
          last_name: user.last_name,
          email_address: user.email_address,
          mobile_number: user.mobile_number,
        });
        setLocationValue({
          country: currentCountryId ? String(currentCountryId) : "",
          state: currentStateId ? String(currentStateId) : "",
          city: currentCityId ? String(currentCityId) : "",
          area: "",
        });
      } catch (error) {
        console.error("Error fetching user:", error);
      }
    }
  }, [baseUrl, loggedInUserToken, userId]);

  const fetchServices = useCallback(async () => {
    try {
      const res = await axios.get(`${baseUrl}/admin/services`, {
        headers: {
          Authorization: `Bearer ${loggedInUserToken}`,
        },
      });
      setServices(res.data.services || []);
    } catch {
      setServices([]);
    }
  }, [baseUrl, loggedInUserToken]);

  useEffect(() => {
    if (!userId) {
      return;
    }

    const loadInitialData = async () => {
      await fetchUserData();
      await fetchServices();
    };

    loadInitialData();
  }, [fetchServices, fetchUserData, userId]);

  const saveProfile = async (updatedProfile) => {
    setProfile(updatedProfile);

    try {
      await editUserApi.editData(updatedProfile);
    } catch (error) {
      console.error("Error updating profile:", error);
      showToast("Error updating profile", { type: "error" });
    }
  };

  const handleLocationChange = (updatedValue) => {
    setLocationValue(updatedValue);
    saveProfile({
      ...profile,
      country_id: updatedValue.country || "",
      state_id: updatedValue.state || "",
      city_id: updatedValue.city || "",
      area_id: updatedValue.area || "",
    });
  };

  const handleServiceChange = (event) => {
    saveProfile({
      ...profile,
      service_id: event.target.value,
    });
  };

  return (
    <>
      <Notification />
      <Row className="mb-3">
        <Col md={8}>
          <LocationSelector
            value={locationValue}
            onChange={handleLocationChange}
            levels={["state", "city"]}
          />
        </Col>

        <Col md={4}>
          <Form.Group>
            <Form.Label>Service</Form.Label>
            <Form.Select
              value={profile.service_id}
              onChange={handleServiceChange}
            >
              <option value="">Select Service</option>
              {services.map((service) => (
                <option key={service.id} value={service.id}>
                  {service.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>
    </>
  );
};

export default LocationServiceSelector;
